import type { JSX } from "react";

import { Stack } from "@similarweb/ui-components/foundations";

import { quickSearchDataAutomation } from "app/components/React/QuickSearch/dataAutomation";
import type { QuickSearchCatalogPage } from "app/components/React/QuickSearch/pageData/getQuickSearchCatalogPages";
import { useReportCatalogSearchMatches } from "app/components/React/QuickSearch/QuickSearchAllTabPanel/useReportCatalogSearchMatches";
import { QuickSearchReportRow } from "app/components/React/QuickSearch/QuickSearchReportRow";
import { QuickSearchTabEmptyState } from "app/components/React/QuickSearch/QuickSearchTabEmptyState";

type QuickSearchReportsTabPanelProps = {
    searchQuery: string;
    defaultReportRows: JSX.Element[];
    onCatalogReportNavigate: (page: QuickSearchCatalogPage, state: string) => void;
};

export const QuickSearchReportsTabPanel = ({
    searchQuery,
    defaultReportRows,
    onCatalogReportNavigate,
}: QuickSearchReportsTabPanelProps) => {
    const { trimmedSearch, isSearching, reportCatalogMatches, hasReportSearchHits } =
        useReportCatalogSearchMatches(searchQuery);

    if (!isSearching) {
        if (defaultReportRows.length === 0) {
            return (
                <QuickSearchTabEmptyState
                    dataAutomation={quickSearchDataAutomation.tabEmptyReports}
                    searchQuery={trimmedSearch}
                />
            );
        }

        return (
            <Stack
                gap="8px"
                padding="16px"
                width="100%"
                data-automation={quickSearchDataAutomation.reportsTabList}
            >
                {defaultReportRows}
            </Stack>
        );
    }

    if (!hasReportSearchHits) {
        return (
            <QuickSearchTabEmptyState
                dataAutomation={quickSearchDataAutomation.tabEmptyReports}
                searchQuery={trimmedSearch}
            />
        );
    }

    return (
        <Stack
            gap="8px"
            paddingX="16px"
            paddingY="12px"
            width="100%"
            data-automation={quickSearchDataAutomation.reportsTabList}
        >
            {reportCatalogMatches.map((page, index) => (
                <QuickSearchReportRow
                    key={`reports-tab-search-${page.id}`}
                    title={page.title}
                    description={page.breadcrumbs.join(" > ")}
                    summarySnippet={page.summary}
                    highlightQuery={trimmedSearch}
                    href={page.url}
                    isExternal={page.isExternal}
                    isLocked={page.isLocked}
                    dataAutomation={quickSearchDataAutomation.reportRowReportsTabSummarySearch(index)}
                    onClick={() => onCatalogReportNavigate(page, page.isLocked ? "locked" : "search_result")}
                />
            ))}
        </Stack>
    );
};
